import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import * as d3 from 'd3';
import DashboardFilters from "../components/DashboardFilters";
import Loading from "../components/Loading";
import Layout from "../components/Layout";
import useAxios from "../hooks/useAxios";
import useDebounced from "../hooks/useDebounced";
import usePersistedState from "../hooks/usePersistedState";
import { TICKET_API_URL, VIEWS_API_URL } from "../environmentVariables";
import { PageProps, Views, Query } from "../helpers/interfaceHelper";
import useFilters from "../hooks/useFilters";
import useQuery from "../hooks/useQuery";
import useView from "../hooks/useView";
import TicketTypeSummary from "../components/TicketTypeSummary";
import DashboardDataTable from "../components/DashboardDataTable";

export interface TicketStatuses {
    status: string;
    count: number;
}

export interface TicketPriority {
    priority: string;
    count: number;
}

export interface TicketClient {
    client: string;
    count: number;
}

export interface TicketAgent {
    agent: string;
    count: number;
}

export interface ChartData
{
    name: string,
    count: number
}

interface DashboardResponse {
    ticketStatuses: TicketStatuses[];
    ticketPriorities: TicketPriority[];
    ticketAgents: TicketAgent[];
    ticketClients: TicketClient[];
    overdueTickets: number;
    dueTodayTickets: number;
    openTickets: number;
    onHoldTickets: number;
    unassignedTickets: number;
    unresolvedTickets: number;
}


function CreatePieChart(chart : React.MutableRefObject<HTMLDivElement | null>, data: ChartData[], darkMode: boolean)
{
    if(!chart.current) return;
    
    chart.current.innerHTML = '';

    if(!data) return;
    
    if(data.length === 0) return;

    const totalStatusCount = data.map(o => o.count).reduce((a, c) => { return a + c; });

    if(totalStatusCount === 0) return;

    const textVisibleOverAmount = ((totalStatusCount/100)*10);
    //setting up svg container
    const margin = 70;
    const width = chart.current.clientWidth - margin * 2;
    const height = chart.current.clientHeight - margin * 2;
    const radius = height/2;
    const midpointX = (chart.current.clientWidth) /2;
    const midpointY = (chart.current.clientHeight) /2;
    const svg = d3.select(chart.current).append('svg').attr('width', width + 2 * margin)
        .attr('height', height + 2 * margin).append('g').attr('transform', 'translate(' + midpointX + ',' + midpointY + ')');

    // setting up chart
    const formattedData = d3.pie<ChartData>().value(d => d.count)(data);
    const arcGenerator = d3.arc<d3.PieArcDatum<ChartData>>().innerRadius(100).outerRadius(radius);
    const colour = d3.scaleOrdinal<string>().range(d3.schemeSet2);
    
    //setting up svg data
    var slices = svg.selectAll()
      .data(formattedData)
      .join("path")
      .attr("d", d => arcGenerator(d))
      .attr('fill', d => colour(d.data.name))
      .style('opacity', 0.7);

    const piechartInfoTextClass = (darkMode) ? 'pieChartInfoText-light' : 'pieChartInfoText-dark';

    // setting up annotation
    svg.selectAll()
      .data(formattedData)
      .join("text")
      .html(d => `${d.data.name}: (${d.data.count})`)
      .attr('transform', function(d) {
        var c = arcGenerator.centroid(d),
        x = c[0],
        y = c[1],
        // pythagorean theorem for hypotenuse
        h = Math.sqrt(x*x + y*y);
        var labelr = height/2 + height*0.1;
        return "translate(" + (x/h * labelr) +  ',' + (y/h * labelr) +  ")"; 
      })
      .attr("text-anchor", function(d) {
          // are we past the center?
          return (d.endAngle + d.startAngle)/2 > Math.PI ? "end" : "start";
      })
      .attr("id", function(d,i) { return d.data.name; })
      .attr("visibility", function(d)
      {
          if(d.data.count < textVisibleOverAmount)
          {
              return "Hidden";
          }
          else
          {
              return "Visible";
          }
      })
      .attr('class', piechartInfoTextClass);


    slices.on('mouseover', function (event, d) 
    {
      d3.select(this)
          .transition()
          .duration(200)
          .attr('transform', 'scale(1.1)');

      if(d?.data?.name !== null)
      {
          var textElement = document.getElementById(d.data.name);
          if(textElement)
          {
              textElement.style.visibility = "visible";
          }
      }
    }).on('mouseout', function (event, d) 
    {
        d3.select(this)
            .transition()
            .duration(200)
            .attr('transform', 'scale(1)');
            
        if(d?.data?.name !== null && d?.data?.count < textVisibleOverAmount)
        {
            var textElement = document.getElementById(d.data.name);
            if(textElement)
            {
                textElement.style.visibility = "hidden";
            }
        }
    });
}

const DashboardPage: React.FC<PageProps> = ({ setDarkMode, darkMode }) => 
{
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 750);
  const [isLoading, setLoading] = useState(false);
  const unresolvedTicketschart = useRef<HTMLDivElement | null>(null);
  const unresolvedTicketsByPrioritychart = useRef<HTMLDivElement | null>(null);
  const [chosenView, setChosenView] = useView();
  const [filters, setFilter, resetFilter] = useFilters([]);
  const [defaultView, setDefaultView] = usePersistedState(
    "Actionable Tickets (SD)",
    "default-dashboard-view"
  );

  //retrieve the views from the relevant API and then use the chosen option
  //to construct query parameters
  const [views, viewsLongLoading] = useAxios<Views>(`${VIEWS_API_URL}/DashboardViews`);

  //when we receive the list of views from the API, we want to make sure the default is selected
  useEffect(() => {
    setChosenView(defaultView, views.views);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views.views]);

    useEffect(() => {

      const replaceClassName = darkMode ? 'pieChartInfoText-dark' : 'pieChartInfoText-light';
      const newClassName = darkMode ? 'pieChartInfoText-light' : 'pieChartInfoText-dark';

      const elements = document.getElementsByClassName(replaceClassName);

      for(var i = (elements.length - 1); i >= 0; i--)
      {
          elements[i].classList.replace(replaceClassName, newClassName);
      }
      
  }, [darkMode]);

  //each time the page is rendered, we want to generate the query to be used to retrieve data from the api
  const query: Query = useQuery({
    sortDirection: 1,
    sortProperty: "dateDue",
    page: 1,
    rowsPerPage: 30,
    filters,
    search: debouncedSearch,
    view: chosenView,
    views: views.views,
  });

    //we now use axios to send the query rather than feathers
    const [axiosData, dataLongLoading] = useAxios<DashboardResponse>(
      `${TICKET_API_URL}/Dashboard`,
      query,
      60000
    );

    //as data is loaded, the loadingData state should be modified. If we are currently loading data,
    //we set the loading state in order to display the loading page
    useEffect(() => {
      setLoading(viewsLongLoading || dataLongLoading);
    }, [viewsLongLoading, dataLongLoading]);

    useLayoutEffect(() => {

      if(!axiosData) return;

      if(axiosData.ticketStatuses !== null)
      {
          const ticketStatusesData: ChartData[] = axiosData.ticketStatuses?.map(s =>  ({name: s.status, count: s.count})); 
          CreatePieChart(unresolvedTicketschart, ticketStatusesData, darkMode)
      }

      if(axiosData.ticketPriorities !== null)
      {
          const ticketPriorityData: ChartData[] = axiosData.ticketPriorities?.map(s => ({ name: s.priority, count: s.count}));
          CreatePieChart(unresolvedTicketsByPrioritychart, ticketPriorityData, darkMode)
      }

    }, [axiosData, darkMode]);

    const labelsEnabled = views?.views?.find((v) => v.id === chosenView)?.labelsEnabled ?? false;

     return (
         <>
          {isLoading && <Loading />}
            <Layout
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                 drawer={
                    <DashboardFilters
                      setFilter={setFilter}
                      resetFilter={resetFilter}
                      dbViews={views.views}
                      view={chosenView}
                      setView={setChosenView}
                      setDefaultView={setDefaultView}
                      search={search}
                      setSearch={setSearch}
                      query={query}
                      labelsEnabled={labelsEnabled}
                    />
                 }
            >
              <div style={{width:'100%',  height:'100px', display: 'table-row'}}>
                <TicketTypeSummary
                count={axiosData.overdueTickets}
                name="Overdue Tickets"/>

                <div style={{ width:'1%',  height:'100%', display: 'table-cell' }}></div>

                <TicketTypeSummary
                count={axiosData.dueTodayTickets}
                name="Tickets Due Today"/>

                <div style={{ width:'1%',  height:'100%', display: 'table-cell' }}></div>

                <TicketTypeSummary
                count={axiosData.openTickets}
                name="Open Tickets"/>

                <div style={{ width:'1%',  height:'100%', display: 'table-cell' }}></div>

                <TicketTypeSummary
                count={axiosData.onHoldTickets}
                name="Tickets On Hold"/>

                <div style={{ width:'1%',  height:'100%', display: 'table-cell' }}></div>

                <TicketTypeSummary
                count={axiosData.unresolvedTickets}
                name="Unresolved Tickets"/>

                <div style={{ width:'1%',  height:'100%', display: 'table-cell' }}></div>

                <TicketTypeSummary
                count={axiosData.unassignedTickets}
                name="Unassigned Tickets"/>

              </div>
                <div style={{width:'100%',  height:'520px'}}>
                    <div style={{width:'100%',  height:'20px', paddingTop: '20px'}}></div> 

                    <div style={{ borderRadius: '15px', border: '2px solid rgb(79, 189, 219)', width:'700px', height:'500px',float:'left'}}>
                      <h1 style={{textAlign: 'center', height: '5%'}}>Tickets by Status</h1>
                      <div style={{width:'90%',  height:'90%', marginLeft: '5%'}} ref={unresolvedTicketschart}></div>
                    </div>

                    <div style={{ width:'1%',  height:'20px', float:'left' }}></div>

                    <div style={{ borderRadius: '15px', border: '2px solid rgb(79, 189, 219)', width:'700px',  height:'500px', float:'left' }}>
                      <h1 style={{textAlign: 'center', height: '5%'}}>Tickets by Priority</h1>
                      <div style={{width:'90%',  height:'90%', marginLeft: '5%'}} ref={unresolvedTicketsByPrioritychart}></div>
                    </div>
                </div>  
                 <div style={{width:'100%', height:'100%'}}>
                    <div style={{ width:'700px', height:'500px',float:'left'}}>
                      <DashboardDataTable
                      title="Agents"
                      dataList={axiosData.ticketAgents?.map(s => ({ name: s.agent, count: s.count}))}
                      />
                      </div>

                    <div style={{ width:'1%',  height:'20px', float:'left' }}></div>

                    <div style={{ width:'700px', height:'500px',float:'left'}}>
                      <DashboardDataTable
                      title="Clients"
                      dataList={axiosData.ticketClients?.map(s => ({ name: s.client, count: s.count}))}
                      />
                    </div>
                      
                 </div>
            </Layout>
        </>
     );
};

export default DashboardPage